<template>
    <div>
        <tsoft-table
            name="cold-client"
            :key="componentRefreshKey"
            :api="tableApiUrl"
            :items.sync="items"
            :isNeedSearch="true"
            :additionalGetParameter="additionalGetParameter"
            :isNeedCreate="isNeedCreate"
            :typeOfTableFilter="typeOfTableFilter"
            :onDataEmptyMessage="'Клиенты по заданному фильтру отсутствуют. Попробуйте снова.'"
            @show="showItem"
            @edit="createOrEditItemModal"
            @delete="deleteItem"
            @openAssignModal="openAssignModal"
            @openStatisticsSidebar="openStatisticsSidebar"
            @openImportSidebar="openImportSidebar"
            @evaluation="openEvaluationModal"
        />

        <b-overlay
            :show="dataOverlay"
            spinner-variant="success"
            no-wrap
        ></b-overlay>

        <b-sidebar
            v-if="isSidebarOpen"
            v-model="isSidebarOpen"
            id="sidebar-right-create-or-edit-cold-client"
            right
            backdrop
            shadow
            width="65em"
            backdrop-variant="dark"
            ref="editItem"
            no-close-on-backdrop
            @change="closeSidePanelCallback()"
        >
            <template #title>
                <div>
                    <strong id="sidebar-right___title__" v-html="modalTitle()"></strong>
                </div>
            </template>
            <div class="d-block">
                <ColdClientModal 
                    :value="isSidebarOpen"
                    :client="editedItem" 
                    @input="isSidebarOpen = $event"
                    @saved="profileUpdated"
                />
            </div>
        </b-sidebar>

        <!-- Сайдбар назначения клиентов -->
        <b-sidebar
            v-if="isAssignSidebarOpen"
            v-model="isAssignSidebarOpen"
            id="sidebar-right-assign-cold-clients"
            right
            backdrop
            shadow
            width="50em"
            backdrop-variant="dark"
            no-close-on-backdrop
        >
            <template #title>
                <div>
                    <strong>Назначить клиентов агенту</strong>
                </div>
            </template>
            <div class="d-block">
                <ColdClientAssignModal 
                    :value="isAssignSidebarOpen"
                    @input="isAssignSidebarOpen = $event"
                    @assigned="onClientsAssigned"
                />
            </div>
        </b-sidebar>

        <!-- Сайдбар оценки клиента -->
        <b-sidebar
            v-if="isEvaluationSidebarOpen"
            v-model="isEvaluationSidebarOpen"
            id="sidebar-right-evaluation-cold-client"
            right
            backdrop
            shadow
            width="50em"
            backdrop-variant="dark"
            no-close-on-backdrop
        >
            <template #title>
                <div>
                    <strong>Оценка клиента</strong>
                </div>
            </template>
            <div class="d-block">
                <ColdClientEvaluationModal 
                    :value="isEvaluationSidebarOpen"
                    :client="evaluationClient"
                    @input="isEvaluationSidebarOpen = $event"
                    @saved="onEvaluationSaved"
                    @clientUpdated="onClientDataUpdated"
                />
            </div>
        </b-sidebar>

        <!-- Сайдбар статистики холодных клиентов -->
        <b-sidebar
            v-if="isStatisticsSidebarOpen"
            v-model="isStatisticsSidebarOpen"
            id="sidebar-right-statistics-cold-clients"
            right
            backdrop
            shadow
            width="75em"
            backdrop-variant="dark"
            no-close-on-backdrop
        >
            <template #title>
                <div>
                    <strong>Статистика по холодным клиентам</strong>
                </div>
            </template>
            <div class="d-block">
                <ColdClientStatistics />
            </div>
        </b-sidebar>

        <!-- Сайдбар импорта холодных клиентов -->
        <b-sidebar
            v-if="isImportSidebarOpen"
            v-model="isImportSidebarOpen"
            id="sidebar-right-import-cold-clients"
            right
            backdrop
            shadow
            width="80em"
            backdrop-variant="dark"
            no-close-on-backdrop
        >
            <template #title>
                <div>
                    <strong>Импорт холодных клиентов</strong>
                </div>
            </template>
            <div class="d-block">
                <ColdClientImportModal 
                    :value="isImportSidebarOpen"
                    @input="isImportSidebarOpen = $event"
                    @imported="onImportCompleted"
                />
            </div>
        </b-sidebar>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import {API_COLD_CLIENTS} from "@/constants/api";
    import ColdClientModal from "@/components/coldClients/ColdClientModal";
    import ColdClientAssignModal from "@/components/coldClients/ColdClientAssignModal";
    import ColdClientEvaluationModal from "@/components/coldClients/ColdClientEvaluationModal";
    import ColdClientStatistics from "@/components/coldClients/ColdClientStatistics";
    import ColdClientImportModal from "@/components/coldClients/ColdClientImportModal";

    import {
        defaultDataItems,
        actionShowItem,
        actionCreateOrUpdateItem,
        actionDeleteItem
    } from '@/mixins';

    const initialEditedItem = () => ({
        id: null,
        company_name: '',
        inn: '',
        email: '',
        website: '',
        phones: [''],
        industry: '',
        region_id: '',
        city_id: '',
        address: '',
        contact_person: '',
        position: '',
        employees_count: '',
        annual_revenue: '',
        decision_maker: '',
        cooperation_status: 'new',
        data_source: '',
        next_contact_at: '',
        comment: '',
        notes: '',
        agent_company_id: null,
        assigned_at: null,
        city: {},
        agent_company: {}
    })

    export default {
        components: {
            ColdClientModal,
            ColdClientAssignModal,
            ColdClientEvaluationModal,
            ColdClientStatistics,
            ColdClientImportModal
        },    
        mixins: [
            defaultDataItems,
            actionShowItem,
            actionDeleteItem,
            actionCreateOrUpdateItem
        ],
        props: {
            isNeedCreate: { type: Boolean, required: false, default: true },        // If table needs create new element button
            additionalGetParameter: { type: String, required: false, default: '' }, // If we want to add something, for example, &agent_company_id=10
            typeOfTableFilter: { type: String, required: false, default: '' },      // Type of filter for output: coldClients
        },
        data() {
            return {
                loading: false,
                tableApiUrl: API_COLD_CLIENTS,
                componentRefreshKey: 0,
                dataOverlay: false,
                isAssignSidebarOpen: false,
                isEvaluationSidebarOpen: false,
                evaluationClient: null,
                isStatisticsSidebarOpen: false,
                isImportSidebarOpen: false
            }
        },
        computed: {
            ...mapGetters('auth', ['currentUser']),

            canAssignClients() {
                const userRoles = this.currentUser.role || []
                return userRoles.includes('administrator') || userRoles.includes('president')
            }
        },
        watch: {
        },
        mounted() {
            this.editedItem = initialEditedItem();
            this.initialAssign();
        },
        methods: {
            initialAssign() {
                // Инициализация данных
            },
            // calls from mixin before sidebaropened
            beforeSidebarOpenedCallback() {
                this.editedItem = initialEditedItem();
                this.initialAssign();
            },
            // calls from mixin on item save
            onCreatedOrUpdatedCallback() {
                this.isSidebarOpen = false
            },
            profileUpdated(data) {
                // If editIndex > -1 → update existing row; else → prepend new row
                if (this.editIndex > -1 && this.items && this.items.data && this.items.data[this.editIndex]) {
                    Object.assign(this.items.data[this.editIndex], data);
                } else if (this.items && this.items.data) {
                    this.items.data.unshift(data);
                }

                // Close sidebar and reset state
                this.onCreatedOrUpdatedCallback();
                this.editIndex = -1;
                this.validationErrors = '';
                this.makeToast('success');
            },
            onDeletedCallback() {
                this.componentRefreshKey++
                this.updateParentData()
            },
            modalTitle() {
                let title = 'Добавление холодного клиента'
                if (this.editIndex > -1) {
                    title = 'Редактирование клиента #' + this.editedItem.id
                }

                return title
            },
            updateParentData() {
                this.$emit("updateParentData")
            },
            // Метод для открытия сайдбара назначения
            openAssignSidebar() {
                this.isAssignSidebarOpen = true;
            },
            onClientsAssigned() {
                this.isAssignSidebarOpen = false;
                this.componentRefreshKey++;
                this.updateParentData();
            },
            
            openAssignModal() {
                this.openAssignSidebar();
            },

            openStatisticsSidebar() {
                this.isStatisticsSidebarOpen = true;
            },

            openImportSidebar() {
                this.isImportSidebarOpen = true;
            },
        
            // Методы для оценки клиентов
            openEvaluationModal(index) {
                const item = this.items.data[index];
                this.evaluationClient = item;
                this.isEvaluationSidebarOpen = true;
            },
            
            // Обработка сохранения оценки
            onEvaluationSaved(updatedClient) {
                // Обновляем данные в таблице
                const index = this.items.data.findIndex(item => item.id === updatedClient.id);
                if (index !== -1) {
                    this.$set(this.items.data, index, updatedClient);
                }
                
                this.isEvaluationSidebarOpen = false;
                this.evaluationClient = null;
            },
            onClientDataUpdated(updatedClient) {
                // Обновляем данные в таблице
                const index = this.items.data.findIndex(item => item.id === updatedClient.id);
                if (index !== -1) {
                    this.$set(this.items.data, index, updatedClient);
                }
                
                // Обновляем данные в evaluationClient для отображения в сайдбаре
                if (this.evaluationClient && this.evaluationClient.id === updatedClient.id) {
                    this.evaluationClient = updatedClient;
                }
            },
            
            // Обработка завершения импорта
            onImportCompleted(importData) {
                this.isImportSidebarOpen = false;
                this.componentRefreshKey++;
                this.updateParentData();
                
                this.$bvToast.toast(
                    `Импорт завершен: ${importData.success} клиентов добавлено`,
                    {
                        title: 'Импорт завершен',
                        variant: 'success'
                    }
                );
            }
        }
    }
</script>




